import { createBrowserRouter, RouterProvider } from "react-router-dom";

import Layout from "./components/Layout";
// import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

// Layouts
import AdminLayout from "./components/layouts/AdminLayout";
import VendorLayout from "./components/layouts/VendorLayout";
import UserDashboardLayout from "./components/layouts/UserDashboardLayout";

// Pages
import LandingPage from "./pages/LandingPage";
import WalletPage from "./pages/WalletPage";
import SpotTradingPage from "./pages/SpotTradingPage";
import P2PPage from "./pages/p2p/P2PPage";
import MarketsPage from "./pages/MarketsPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import OtpVerificationPage from "./pages/auth/OtpVerificationPage";

// Profile Pages
import ProfilePage from "./pages/profile/ProfilePage";
import KycPage from "./pages/profile/KycPage";
import SettingsPage from "./pages/profile/SettingsPage";

// Footer Pages
import AboutPage from "./pages/footer/AboutPage";
import TermsPage from "./pages/footer/TermsPage";
import PrivacyPage from "./pages/footer/PrivacyPage";
import HelpPage from "./pages/footer/HelpPage";

// Admin Pages - Content only (no layout wrapper)
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminWallets from "./pages/admin/AdminWallets";
import AdminTrades from "./pages/admin/AdminTrades";
import AdminDisputes from "./pages/admin/AdminDisputes";
import AdminTokens from "./pages/admin/AdminTokens";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminCompliance from "./pages/admin/AdminCompliance";
import AdminReports from "./pages/admin/AdminReports";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminSettings from "./pages/admin/AdminSettings";

// User Dashboard Pages - Content only (no layout wrapper)
import UserDashboard from "./pages/dashboard/UserDashboard";
import UserHistory from "./pages/dashboard/UserHistory";
// import UserRewards from "./pages/dashboard/UserRewards";
import UserAlerts from "./pages/dashboard/UserAlerts";

// Forex Market Pages
import ForexHomePage from "./pages/forex/ForexHomePage";
import ForexListingsPage from "./pages/forex/ForexListingsPage";
import ListingDetailPage from "./pages/forex/ListingDetailPage";

// Forex Vendor Pages - Content only (no layout wrapper)
import VendorDashboard from "./pages/forex/vendor/VendorDashboard";
import VendorListings from "./pages/forex/vendor/VendorListings";
import VendorMessages from "./pages/forex/vendor/VendorMessages";
import VendorReviews from "./pages/forex/vendor/VendorReviews";
import VendorAnalytics from "./pages/forex/vendor/VendorAnalytics";
import VendorWallet from "./pages/forex/vendor/VendorWallet";
import VendorDocuments from "./pages/forex/vendor/VendorDocuments";
import VendorSettings from "./pages/forex/vendor/VendorSettings";
import VendorBanks from "./pages/forex/vendor/VendorBanks";
import ListingFormPage from "./pages/forex/ListingFormPage";

import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import { useNotificationSSE } from "./utils/useNotificationSSE";
import { UserRole } from "./interfaces/profile";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import SmoothScroll from "./components/SmoothScroll";
import VendorListingDetailPage from "./pages/forex/vendor/VendorListingDetailPage";
import { Toaster } from "sonner";
import TwoFALoginPage from "./pages/auth/TwoFALoginPage";
import AminWalletDetailsPage from "./pages/admin/AminWalletDetailsPage";
import AdminTransactions from "./pages/admin/AdminTransactions";
import { useEffect } from "react";
import { publicRequest } from "./utils/makeRequest";
import { loginFailure } from "./store/slices/authSlice";
import { fetchSettings } from "./store/actions/settingsActions";
import { useAppDispatch } from "./store/hooks";
import UserP2P from "./pages/profile/UserP2P";
import UserSupport from "./pages/dashboard/UserSupport";
import AdFormPage from "./pages/profile/AdFormPage";
import P2pLayout from "./components/layouts/P2pLayout";
import AdminTransactionDetails from "./pages/admin/AdminTransactionDetails";
import { TradeView } from "./pages/p2p/TradeView ";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AssetsManagementPage from "./pages/AssetManagementPage";
import UserAssets from "./pages/profile/UserAssets";
import AssetDetails from "./pages/dashboard/AssetDetails";
import EditAdvertisementPage from "./pages/p2p/EditAdvertisementPage";
import { tradeSocket } from "./services/tradeSocket";
import AdminUser from "./pages/admin/AdminUser";
import AdminUserEdit from "./pages/admin/AdminUserEdit";
import MerchantProfilePage from "./pages/p2p/MerchantProfilePage";
import AdminComplianceDetails from "./pages/admin/AdminComplianceDetails";
import AdminTransfer from "./pages/admin/AdminTransfer";
import UserTransfer from "./pages/profile/UserTransfer";
import AdminSwap from "./pages/admin/AdminSwap";
import UserSwap from "./pages/profile/UserSwap";
import UserHomePage from "./pages/profile/UserHomePage";
import AdminMint from "./pages/admin/AdminMint";
import AdminWithdrawal from "./pages/admin/AdminWithdrawal";
import UserWithdrawal from "./pages/profile/UserWithdrawal";
import AdminBlockchainNetwork from "./pages/admin/AdminBlockchainNetwork";
import AdminDeposit from "./pages/admin/AdminDeposit";
import UserDeposit from "./pages/profile/UserDeposit";
import AuthOnlyRoute from "./routes/AuthOnlyRoute";
import AdminTransactionTierSettings from "./pages/admin/AdminTransactionTierSettings";
import { useAdvertisementSSE } from "./utils/useAdvertisementSSE";
import AdminDisputeDetails from "./pages/admin/AdminDisputeDetails";
import TransactionPasswordSetupPage from "./pages/auth/TransactionPasswordSetupPage";
import TransactionPasswordResetPage from "./pages/auth/TransactionPasswordResetPage ";
import Convert from "./pages/Convert";
import NetworkStatus from "./components/shared/NetworkStatus";
import UserTrades from "./pages/profile/UserTrades";

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "/",
        element: <LandingPage />,
      },
      { path: "/markets", element: <MarketsPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/login", element: <LoginPage /> },

      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password/:resetToken", element: <ResetPasswordPage /> },
      { path: "/verify-otp", element: <OtpVerificationPage /> },
      { path: "/two-factor", element: <TwoFALoginPage /> },
      { path: "/about", element: <AboutPage /> },
      { path: "/terms", element: <TermsPage /> },
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/help", element: <HelpPage /> },
      { path: "/convert", element: <Convert /> },
      // { path: "/forex", element: <ForexHomePage /> },
      { path: "/forex/listings", element: <ForexListingsPage /> },
      { path: "/forex/listing/:id", element: <ListingDetailPage /> },
      { path: "/spot", element: <SpotTradingPage /> },

      {
        element: <AuthOnlyRoute />,
        children: [
          {
            path: "/setup/transaction-password",
            element: <TransactionPasswordSetupPage />,
          },
          {
            path: "/setup/transaction-password/reset",
            element: <TransactionPasswordResetPage />,
          },
        ],
      },
    ],
  },

  {
    path: "/p2p",
    element: (
      <ProtectedRoute
        allowedRoles={[
          UserRole.USER,
          UserRole.ADMIN,
          UserRole.COMPLIANCE,
          UserRole.DISPUTE_MODERATOR,
          UserRole.SUPPORT,
          UserRole.VIP_USER,
          UserRole.SUPER,
        ]}
      />
    ),
    children: [
      {
        element: <P2pLayout />,
        children: [
          {
            index: true,
            element: <P2PPage />,
          },
          {
            path: ":id",
            element: <TradeView />,
          },
          {
            path: "merchant/:merchantId",
            element: <MerchantProfilePage />,
          },
          {
            path: "wallet",
            element: <WalletPage />,
          },
        ],
      },
    ],
  },

  {
    path: "/vendor",
    element: <ProtectedRoute allowedRoles={[UserRole.USER]} />,
    children: [
      {
        element: <VendorLayout />,
        children: [
          { index: true, element: <VendorDashboard /> },
          { path: "listings", element: <VendorListings /> },
          { path: "listing/new", element: <ListingFormPage /> },
          { path: "listing/edit/:id", element: <ListingFormPage /> },
          { path: "listing/:id", element: <VendorListingDetailPage /> },
          { path: "messages", element: <VendorMessages /> },
          { path: "reviews", element: <VendorReviews /> },
          { path: "analytics", element: <VendorAnalytics /> },
          { path: "wallet", element: <VendorWallet /> },
          { path: "banks", element: <VendorBanks /> },
          { path: "documents", element: <VendorDocuments /> },
          { path: "settings", element: <VendorSettings /> },
        ],
      },
    ],
  },

  {
    path: "/dashboard",
    element: <ProtectedRoute allowedRoles={[UserRole.USER]} />,
    children: [
      {
        element: <UserDashboardLayout />,
        children: [
          { index: true, element: <UserHomePage /> },
          { path: "wallet", element: <WalletPage /> },
          { path: "profile", element: <UserDashboard /> },
          { path: "wallet/:id", element: <AssetDetails /> },
          { path: "transactions", element: <UserHistory /> },
          { path: "support", element: <UserSupport /> },
          { path: "ad-form", element: <AdFormPage /> },
          { path: "p2p", element: <UserP2P /> },
          { path: "trades", element: <UserTrades /> },
          { path: "p2p/ads/:id/edit", element: <EditAdvertisementPage /> },
          // { path: "rewards", element: <UserRewards /> },
          { path: "alerts", element: <UserAlerts /> },
          { path: "kyc", element: <KycPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "search", element: <AssetsManagementPage /> },
          { path: "assets", element: <UserAssets /> },
          { path: "transfer", element: <UserTransfer /> },
          { path: "swap", element: <UserSwap /> },
          { path: "withdraw", element: <UserWithdrawal /> },
          { path: "deposit", element: <UserDeposit /> },
        ],
      },
    ],
  },

  {
    path: "/admin",
    element: (
      <ProtectedRoute
        allowedRoles={[
          UserRole.ADMIN,
          UserRole.SUPER,
          // UserRole.COMPLIANCE,
          // UserRole.DISPUTE_MODERATOR,
          // UserRole.SUPPORT,
        ]}
      />
    ),
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: "users", element: <AdminUsers /> },
          { path: "users/:id", element: <AdminUser /> },
          { path: "users/edit/:id", element: <AdminUserEdit /> },
          { path: "wallets", element: <AdminWallets /> },
          { path: "wallets/:systemType", element: <AminWalletDetailsPage /> },
          { path: "trades", element: <AdminTrades /> },
          { path: "disputes", element: <AdminDisputes /> },
          { path: "disputes/:id", element: <AdminDisputeDetails /> },
          { path: "tokens", element: <AdminTokens /> },
          { path: "networks", element: <AdminBlockchainNetwork /> },
          { path: "analytics", element: <AdminAnalytics /> },
          { path: "compliance", element: <AdminCompliance /> },
          { path: "compliance/:id", element: <AdminComplianceDetails /> },
          { path: "reports", element: <AdminReports /> },
          { path: "support", element: <AdminSupport /> },
          { path: "logs", element: <AdminLogs /> },
          { path: "settings", element: <AdminSettings /> },
          { path: "transactions", element: <AdminTransactions /> },
          { path: "transactions/:id", element: <AdminTransactionDetails /> },
          { path: "notifications", element: <AdminNotifications /> },
          { path: "transfer/:systemType", element: <AdminTransfer /> },
          { path: "swap/:systemType", element: <AdminSwap /> },
          { path: "mint/:systemType", element: <AdminMint /> },
          { path: "withdraw/:systemType", element: <AdminWithdrawal /> },
          { path: "deposit/:systemType", element: <AdminDeposit /> },
          {
            path: "transaction-tier-settings",
            element: <AdminTransactionTierSettings />,
          },
        ],
      },
    ],
  },

  { path: "*", element: <NotFound /> },
]);

function App() {
  const dispatch = useAppDispatch();

  useNotificationSSE();
  useAdvertisementSSE();

  useEffect(() => {
    tradeSocket.connect();
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await publicRequest.post("/auth/refresh");

        dispatch(fetchSettings());
      } catch {
        dispatch(loginFailure("Session expired"));
      }
    };

    bootstrap();
  }, []);

  return (
    <div className="w-full flex items-center justify-center">
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors />
      <NetworkStatus />
    </div>
  );
}

export default App;
